import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, CategoryProvider, LanguageProvider, SearchProvider, ThemeProvider, VoteProvider, useAuth } from './contexts';
import msalInstance from './config/msal';
import { Header } from '@/components/Header';
import { Dashboard } from '@/components/Dashboard';
import { PromptDetail } from '@/components/PromptDetail';
import { AuthPage } from '@/components/auth/AuthPage';
import VerifyEmailPage from '@/components/auth/VerifyEmailPage';
import VerifyNewEmailPage from '@/components/auth/VerifyNewEmailPage';
import SSOCallback from '@/components/auth/SSOCallback';
import ForgotPasswordPage from '@/components/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/components/auth/ResetPasswordPage';
import SettingsPage from '@/components/SettingsPage';
import BillingPage from '@/components/BillingPage';
import UpdateEmailPage from '@/components/UpdateEmailPage';
import TeamsWaitlistPage from '@/components/waitlist/TeamsWaitlistPage';
import VerifyEmailUpdatePage from '@/components/VerifyEmailUpdatePage';
import UsageDashboardPage from '@/components/UsageDashboardPage';
import PublicDiscoverPage from '@/components/PublicDiscoverPage';
import { AnalyticsDashboard } from '@/pages/admin/AnalyticsDashboard';
import { useUsageLimits } from '@/hooks/useUsageLimits';
import { useUsageAlerts } from '@/hooks/useUsageAlerts';
import { usePageView } from '@/hooks/useGTMTracking';
import '@/utils/clearCookies'; // Make cookie clearing utilities available globally
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

// Configure TanStack Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20000,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route wrapper component
const ProtectedRoute: React.FC<{ children: React.ReactNode; }> = ({ children }) => {
  const { authState, isAuthReady: authReadyFlag } = useAuth();

  // Track if user was ever authenticated during this session to prevent ping-pong
  const wasAuthenticatedRef = React.useRef(false);
  if (authState === 'authenticated') {
    wasAuthenticatedRef.current = true;
  }

  // Show loading spinner while authentication state is being determined OR auth not ready
  if (authState === 'loading' || !authReadyFlag()) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
      </div>
    );
  }

  // If user was authenticated at some point, don't redirect on temporary unauthenticated states
  if (authState === 'unauthenticated' && !wasAuthenticatedRef.current) {
    // Save current route for redirect after login
    const currentPath = window.location.pathname;
    if (currentPath !== '/auth' && !currentPath.startsWith('/auth/')) {
      localStorage.setItem('redirectAfterLogin', currentPath);
    }
    return <Navigate to="/auth" replace={true} />;
  }

  // User is authenticated OR was authenticated (handle temporary flips), render the protected content
  return <>{children}</>;
};

const AppContent = () => {
  const { isLoggedIn } = useAuth();
  const { usage } = useUsageLimits();
  const location = useLocation();
  
  // Track page views on route changes
  usePageView(location.pathname);
  

  // Enable usage alerts only when user is logged in
  useUsageAlerts(usage, {
    enabled: isLoggedIn,
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 transition-colors duration-300">
      <Header />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={true} closeOnClick={true} pauseOnHover={true} />
      <main className="container mx-auto px-3 sm:px-4 lg:px-8 pt-2 sm:pt-4 pb-4 sm:pb-8">
        <Routes>

          <Route path="/discover" element={<PublicDiscoverPage />} />

          <Route path="/auth" element={<AuthPage />} />
          <Route path="/auth/:mode" element={<AuthPage />} />
          <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
          <Route path="/auth/verify-new-email" element={<VerifyNewEmailPage />} />
          <Route path="/auth/callback" element={<SSOCallback />} />
          <Route path="/login" element={<Navigate to="/auth/login" />} />
          <Route path="/signup" element={<Navigate to="/auth/signup" />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/teams" element={<TeamsWaitlistPage />} />
          <Route path="/prompts/new" element={<PromptDetail newPrompt={true} />} />
          {/* Dashboard and prompt routes - keep all logic from both branches */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/prompts/:id"
            element={
              <ProtectedRoute>
                <PromptDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <BillingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/usage"
            element={
              <ProtectedRoute>
                <UsageDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/update-email"
            element={
              <ProtectedRoute>
                <UpdateEmailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/verify-email-update"
            element={
              <ProtectedRoute>
                <VerifyEmailUpdatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsDashboard />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <Router>
            <AuthProvider>
              <LanguageProvider>
                <VoteProvider>
                  <SearchProvider>
                    <CategoryProvider>
                      <AppContent />
                    </CategoryProvider>
                  </SearchProvider>
                </VoteProvider>
              </LanguageProvider>
            </AuthProvider>
          </Router>
        </QueryClientProvider>
      </ThemeProvider>
    </MsalProvider>
  );
}

export default App;
